/**
 * The subtitle band (ARCHITECTURE.md §8.6).
 *
 * DOM, not canvas, for one reason: `--stage-scale` shrinks the 1920-px stage
 * to ≈ 0.42 on a landscape phone, so anything sized in stage pixels ends up
 * adult-sized. The band's font is therefore
 * `max(calc(34px × var(--stage-scale)), min_text_px)` **CSS** pixels with a
 * 16 px floor (smaller than §8.6's 48/24 by request), at most 3 lines, on the
 * app player's caption plate — one rounded box hugging the words, which keeps
 * them legible on any picture — and on phones it sits in the letterbox
 * *below* the stage — never over the picture, in either orientation.
 *
 * Karaoke highlighting is a **pure function of story time**:
 * `wordIndexAt(words, tLocal)` returns the last word whose span has started.
 * That keeps §8.1's contract — `seek(t)` highlights exactly the word that
 * playing to `t` would have highlighted — and it works identically for baked
 * Kokoro word timings (`timing: 'native'`) and for the proportional timings
 * the Web Speech fallback estimates (`timing: 'estimated'`,
 * docs/BUILD-OVERRIDES.md). When the speech engine does emit `onboundary`
 * events, `setWordIndex()` overrides the computed index for that line only.
 *
 * The band never renders HTML from story text: every word goes in through
 * `textContent`.
 */

import type { PlayerUiState } from './index'
import { wordIndexAt } from './index'
import type { LineWord } from './webspeech'
import { splitWords } from './webspeech'
import { THEME } from './theme'

/* ------------------------------------------------------------------- pure */

/** §8.6 / `Bundle.options.min_text_px`. */
export const SUBTITLE_MIN_PX = 16
/** The stage-pixel size the floor is derived from (§8.6 said 48; smaller by request). */
export const SUBTITLE_BASE_PX = 34
/** §8.6: the band grows downward to at most three lines. */
export const SUBTITLE_MAX_LINES = 3

/** The word-highlight search of §8.4 — one implementation, in `index.ts`. */
export { wordIndexAt }

/** `max(calc(48px × var(--stage-scale)), min_text_px)`, computed in CSS pixels. */
export function subtitleFontPx(stageScale: number, minPx: number = SUBTITLE_MIN_PX, basePx: number = SUBTITLE_BASE_PX): number {
  const scale = Number.isFinite(stageScale) && stageScale > 0 ? stageScale : 1
  return Math.max(minPx, Math.round(basePx * scale))
}

/**
 * Phones put the band below the stage (§8.6). "Phone" here is a viewport
 * question, not a UA question: the short side decides, so a small window on
 * a desktop behaves like the device it looks like.
 */
export function isPhoneViewport(width: number, height: number): boolean {
  return Math.min(width, height) < 520 || Math.max(width, height) < 900
}

export type SubtitlePlacement = 'below' | 'overlay'

export function placementFor(width: number, height: number): SubtitlePlacement {
  return isPhoneViewport(width, height) ? 'below' : 'overlay'
}

/** What the band needs of a line — a structural subset of `ActiveLine`. */
export interface SubtitleLine {
  id: string
  text: string
  words: readonly LineWord[]
}

/* -------------------------------------------------------------------- DOM */

const STYLE_ID = 'story-player-subtitles'

const CSS = `
.sp-subs{--sp-sub-font:${SUBTITLE_MIN_PX}px;box-sizing:border-box;width:100%;display:flex;align-items:center;justify-content:center;
  padding:calc(var(--sp-sub-font) * 0.5) calc(var(--sp-sub-font) * 0.75);
  font-family:${THEME.font};font-size:var(--sp-sub-font);line-height:1.32;font-weight:500;
  text-align:center;text-wrap:balance;color:#fff;pointer-events:none;-webkit-user-select:none;user-select:none;}
.sp-subs[data-visible="false"]{visibility:hidden;}
.sp-subs[data-placement="overlay"]{position:absolute;left:0;right:0;bottom:0;background:none;}
.sp-subs[data-placement="below"]{position:relative;background:none;}
.sp-subs-text{margin:0;max-width:34ch;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:${SUBTITLE_MAX_LINES};line-clamp:${SUBTITLE_MAX_LINES};overflow:hidden;
  padding:0.25em 0.55em;border-radius:0.45em;background:${THEME.caption};-webkit-box-decoration-break:clone;box-decoration-break:clone;}
.sp-subs-text:empty{display:none;}
.sp-subs-w{transition:color 90ms linear,opacity 90ms linear;color:rgba(255,255,255,.72);}
.sp-subs-w[data-state="spoken"]{color:#fff;}
.sp-subs-w[data-state="active"]{color:${THEME.accent};}
.sp-subs[data-karaoke="false"] .sp-subs-w{color:#fff;}
.sp-subs-label{color:#fff;opacity:.95;}
@media (prefers-reduced-motion:reduce){.sp-subs-w{transition:none;}}
`

function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return
  const style = doc.createElement('style')
  style.id = STYLE_ID
  style.textContent = CSS
  doc.head.appendChild(style)
}

export interface SubtitleBandOptions {
  /** `Bundle.options.min_text_px` (24). */
  minTextPx?: number
  placement?: SubtitlePlacement
  /** Start hidden until the grown-up panel / band default turns it on (§8.6). */
  enabled?: boolean
  document?: Document
}

/**
 * Owns one `<div class="sp-subs">` inside the host the player gives it.
 *
 * The host decides *where* that div lives (inside the stage box for
 * `overlay`, in the letterbox for `below`); the band only styles itself for
 * the placement it was told about, so `player/index.ts` can move it between
 * containers on an orientation change without the band keeping stale state.
 */
export class SubtitleBand {
  readonly element: HTMLElement
  private readonly textEl: HTMLElement
  private readonly doc: Document
  private readonly minTextPx: number
  private lineId: string | null = null
  private spans: HTMLElement[] = []
  private activeIndex = -1
  private enabled: boolean
  private karaoke = true
  private stageScale = 1
  private disposed = false

  constructor(host: HTMLElement, opts: SubtitleBandOptions = {}) {
    this.doc = opts.document ?? host.ownerDocument ?? document
    // The bundle's `min_text_px` (24, from §8.6) would pin the small subtitles back up; it caps at our floor.
    this.minTextPx = Math.min(opts.minTextPx ?? SUBTITLE_MIN_PX, SUBTITLE_MIN_PX)
    this.enabled = opts.enabled ?? true
    ensureStyle(this.doc)
    const el = this.doc.createElement('div')
    el.className = 'sp-subs'
    el.setAttribute('role', 'status')
    el.setAttribute('aria-live', 'polite')
    el.setAttribute('aria-atomic', 'true')
    el.dataset.placement = opts.placement ?? 'overlay'
    el.dataset.visible = 'false'
    el.dataset.karaoke = 'true'
    const text = this.doc.createElement('p')
    text.className = 'sp-subs-text'
    el.appendChild(text)
    this.element = el
    this.textEl = text
    this.setStageScale(1)
    host.appendChild(el)
  }

  /** Subtitles on/off (band default + grown-up panel, §8.6). */
  setEnabled(on: boolean): void {
    this.enabled = on
    this.syncVisibility()
  }

  /**
   * Karaoke off renders the whole line in one colour. §8.6 turns it off for
   * band 3-4 (non-readers), and `timing: 'estimated'` lines may prefer it too
   * — a highlight on guessed boundaries draws the eye to the wrong word.
   */
  setKaraoke(on: boolean): void {
    this.karaoke = on
    this.element.dataset.karaoke = on ? 'true' : 'false'
  }

  setPlacement(placement: SubtitlePlacement): void {
    if (this.element.dataset.placement !== placement) this.element.dataset.placement = placement
  }

  get placement(): SubtitlePlacement {
    return (this.element.dataset.placement as SubtitlePlacement) ?? 'overlay'
  }

  /** CSS px per stage px — `--stage-scale`. Drives the font, with the floor. */
  setStageScale(scale: number): void {
    this.stageScale = scale
    const font = `${subtitleFontPx(scale, this.minTextPx)}px`
    if (this.element.style.getPropertyValue('--sp-sub-font') !== font) this.element.style.setProperty('--sp-sub-font', font)
  }

  get fontPx(): number {
    return subtitleFontPx(this.stageScale, this.minTextPx)
  }

  /**
   * Show a line. Re-showing the same line id is a no-op, so a runner that
   * calls this every frame does not rebuild the DOM 60 times a second.
   */
  show(line: SubtitleLine): void {
    if (this.disposed) return
    if (this.lineId === line.id && this.textEl.childNodes.length > 0) return
    this.lineId = line.id
    this.activeIndex = -1
    this.spans = []
    this.textEl.textContent = ''
    const words = line.words
    if (words.length === 0) {
      this.textEl.textContent = line.text
    } else {
      let cursor = 0
      for (const word of words) {
        const ci = Math.max(cursor, Math.min(word.ci, line.text.length))
        if (ci > cursor) this.textEl.appendChild(this.doc.createTextNode(line.text.slice(cursor, ci)))
        const span = this.doc.createElement('span')
        span.className = 'sp-subs-w'
        span.dataset.state = 'pending'
        span.textContent = line.text.slice(ci, ci + word.cl) || word.w
        this.textEl.appendChild(span)
        this.spans.push(span)
        cursor = ci + (span.textContent?.length ?? word.cl)
      }
      if (cursor < line.text.length) this.textEl.appendChild(this.doc.createTextNode(line.text.slice(cursor)))
    }
    this.syncVisibility()
  }

  /** The id of the line (or `label:<text>`) on show, or null. Lets a per-frame caller skip rebuilding it. */
  get currentLineId(): string | null {
    return this.lineId
  }

  /** A checkpoint label ("The end — again?", "Tap to make Pip splash!", ADDENDUM §5.3). */
  showLabel(label: string): void {
    if (this.disposed) return
    if (this.lineId === `label:${label}` && this.textEl.childNodes.length > 0) return
    this.lineId = `label:${label}`
    this.spans = []
    this.activeIndex = -1
    this.textEl.textContent = ''
    const span = this.doc.createElement('span')
    span.className = 'sp-subs-label'
    span.textContent = label
    this.textEl.appendChild(span)
    this.syncVisibility()
  }

  /** Highlight the word at `tLocal` seconds into the line. Pure in `tLocal`. */
  setTime(tLocal: number, words: readonly LineWord[]): void {
    this.setWordIndex(wordIndexAt(words, tLocal))
  }

  /** Force the highlight (an `onboundary` event from the speech engine). */
  setWordIndex(index: number): void {
    if (this.disposed || !this.karaoke) return
    const clamped = Math.min(index, this.spans.length - 1)
    if (clamped === this.activeIndex) return
    const from = Math.min(this.activeIndex, clamped)
    const to = Math.max(this.activeIndex, clamped)
    for (let i = Math.max(0, from); i <= Math.max(0, to) && i < this.spans.length; i += 1) {
      const span = this.spans[i]
      if (!span) continue
      span.dataset.state = i < clamped ? 'spoken' : i === clamped ? 'active' : 'pending'
    }
    this.activeIndex = clamped
  }

  get wordIndex(): number {
    return this.activeIndex
  }

  /** Text now displayed — the band's own readback, used by the tests. */
  get text(): string {
    return this.textEl.textContent ?? ''
  }

  clear(): void {
    // Called every frame between lines: an empty band stays untouched.
    if (this.lineId === null && this.textEl.firstChild === null) {
      this.syncVisibility()
      return
    }
    this.lineId = null
    this.spans = []
    this.activeIndex = -1
    this.textEl.textContent = ''
    this.syncVisibility()
  }

  dispose(): void {
    this.disposed = true
    this.spans = []
    this.element.remove()
  }

  private syncVisibility(): void {
    const has = this.textEl.firstChild !== null && (this.textEl.textContent ?? '').length > 0
    const visible = this.enabled && has ? 'true' : 'false'
    if (this.element.dataset.visible !== visible) this.element.dataset.visible = visible
  }
}

/* ------------------------------------------------------------- ui adapter */

/**
 * Structural subset of `PlayerUiState` — kept structural on purpose so this
 * file does not break when `player/index.ts` (core track) declares the real
 * one with extra fields.
 */
export interface SubtitleUiLike {
  subtitles: boolean
  stageScale: number
  label?: string | null
  line?: { id: string; text: string; words: readonly LineWord[]; tLocal: number; timing?: 'native' | 'estimated' } | null
}

/** One call per frame from the runner: keeps the band in sync with the state. */
export function applySubtitleState(band: SubtitleBand, ui: SubtitleUiLike): void {
  band.setEnabled(ui.subtitles)
  band.setStageScale(ui.stageScale)
  if (ui.line) {
    band.show({ id: ui.line.id, text: ui.line.text, words: ui.line.words })
    band.setTime(ui.line.tLocal, ui.line.words)
    return
  }
  if (ui.label) {
    band.showLabel(ui.label)
    return
  }
  band.clear()
}

/* --------------------------------------------- the core PlayerUiState seam */

/**
 * Drive the band straight from the runner's published `PlayerUiState`.
 *
 * The core state carries `subtitle: { text, wordIndex, speaker }` — the
 * index is already computed by `index.ts#wordIndexAt` from the baked or
 * estimated timings, so the band only renders it. Pass `words` when the
 * caller has the line's `AudioRef.words[]` and wants the spans to line up
 * with the real punctuation; otherwise the spans are derived from the text.
 *
 * `--stage-scale` is not part of `PlayerUiState`, so the shell keeps calling
 * `setStageScale()` on resize.
 */
export function applyPlayerUiState(band: SubtitleBand, ui: PlayerUiState, words?: readonly LineWord[]): void {
  band.setEnabled(ui.subtitles)
  const sub = ui.subtitle
  if (sub && sub.text) {
    const id = `${ui.sceneIndex}:${sub.speaker ?? 'narrator'}:${sub.text}`
    // Called every frame: the spans are built once per line, not re-split 60 times a second.
    if (band.currentLineId !== id) {
      const spans = words && words.length > 0 ? words : splitWords(sub.text).map((t) => ({ ...t, s: 0, e: 0 }))
      band.show({ id, text: sub.text, words: spans })
    }
    band.setWordIndex(sub.wordIndex)
    return
  }
  if (ui.holdLabel) {
    band.showLabel(ui.holdLabel)
    return
  }
  band.clear()
}
